import { useState, useRef, useEffect } from "react";
import { MoreHorizontal, Eye, Copy, Download, ChevronRight, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import type { Leak } from "../api/leaks";
import EngagementStatusBadge from "./EngagementStatusBadge";
import { cleanTitle, getDisplayForum, isUrlscanLeak } from "../utils/cleaners";
import { KNOWN_RANSOMWARE_GROUPS } from "../utils/xShare";
import XShareModal from "./XShareModal";
import UpgradeToSeeMoreButton from "./UpgradeToSeeMoreButton";
import { isLeakLocked, type MinimalUser } from "../utils/subscription";

interface LeakTableProps {
  leaks: Leak[];
  loading?: boolean;
  isAdmin?: boolean;
  user?: MinimalUser | null;
  hasSearch?: boolean;
  onRowClick?: (leak: Leak, isLocked?: boolean, rank?: number) => void;
  onShare?: (leak: Leak) => void;
  startIndex?: number;
}

// ── Severity ─────────────────────────────────────────────────────────────────
const SEVERITY_STYLES: Record<string, { wrapper: string; dot: string }> = {
  Critical: {
    wrapper: "severity-badge severity-critical",
    dot: "severity-dot dot-critical",
  },
  High: {
    wrapper: "severity-badge severity-high",
    dot: "severity-dot dot-high",
  },
  Medium: {
    wrapper: "severity-badge severity-medium",
    dot: "severity-dot dot-medium",
  },
  Low: {
    wrapper: "severity-badge severity-low",
    dot: "severity-dot dot-low",
  },
  Info: {
    wrapper: "severity-badge severity-info",
    dot: "severity-dot dot-info",
  },
};

function SeverityBadge({ severity }: { severity: string }) {
  const styles =
    SEVERITY_STYLES[severity] ?? {
      wrapper: "severity-badge severity-low",
      dot: "severity-dot dot-low",
    };
  return (
    <span className={styles.wrapper}>
      <span className={styles.dot} />
      {severity}
    </span>
  );
}

// ── Network Badge ─────────────────────────────────────────────────────────────
function NetworkBadge({ network }: { network: string }) {
  const isTor = network?.toLowerCase() === "tor";
  const isI2P = network?.toLowerCase() === "i2p";
  const dotCls = isTor
    ? "net-dot dot-tor"
    : isI2P
      ? "net-dot dot-i2p"
      : "net-dot dot-surface";
  return (
    <div className="flex items-center gap-2">
      <span className={dotCls} />
      <span className="text-sm font-medium text-slate-300 capitalize">
        {network}
      </span>
    </div>
  );
}

// ── Action Menu ───────────────────────────────────────────────────────────────
function ActionMenu({
  leak,
  onViewDetails,
}: {
  leak: Leak;
  onViewDetails: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleCopyLink = () => {
    const url = isUrlscanLeak(leak)
      ? `${window.location.origin}/leaks/${leak._id}`
      : (leak.source_url || `${window.location.origin}/leaks/${leak._id}`);
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
    setOpen(false);
  };

  const handleDownload = () => {
    const data = JSON.stringify(leak, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leak_${leak._id}.json`;
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    document.body.removeChild(a);
    toast.success("Leak data downloaded");
    setOpen(false);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        className="action-menu-trigger p-2 hover:bg-slate-800 rounded-lg transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
      >
        <MoreHorizontal size={18} />
      </button>

      {open && (
        <div
          className="action-menu-dropdown z-50 absolute right-0 mt-2 w-48 bg-slate-900 border border-slate-700 rounded-lg shadow-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white flex items-center gap-2"
            onClick={() => {
              onViewDetails();
              setOpen(false);
            }}
          >
            <Eye size={14} className="text-blue-400" />
            View Details
          </button>
          <button 
            className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white flex items-center gap-2"
            onClick={handleCopyLink}
          >
            <Copy size={14} className="text-emerald-400" />
            Copy Source URL
          </button>
          <button 
            className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white flex items-center gap-2"
            onClick={handleDownload}
          >
            <Download size={14} className="text-amber-400" />
            Download Data
          </button>
        </div>
      )}
    </div>
  );
}

// ── Row ───────────────────────────────────────────────────────────────────────
function LeakRow({
  leak,
  index,
  isAdmin,
  user,
  hasSearch,
  onRowClick,
  onShare,
}: {
  leak: Leak;
  index: number;
  isAdmin?: boolean;
  user?: MinimalUser | null;
  hasSearch?: boolean;
  onRowClick?: (leak: Leak, isLocked?: boolean, rank?: number) => void;
  onShare?: (leak: Leak) => void;
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isLocked = isLeakLocked(leak, index, user, hasSearch);

  const resolveStr = (val: any): string => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (Array.isArray(val)) return val.filter(Boolean).map(String).join(" ");
    if (typeof val === "object") return String(val.title || val.name || "");
    return String(val);
  };

  const rawTitle =
    cleanTitle(resolveStr(leak.company_name)) ||
    cleanTitle(resolveStr(leak.title)) ||
    cleanTitle(resolveStr(leak.thread_title)) ||
    cleanTitle(resolveStr(leak.thread_title_list)) ||
    cleanTitle(resolveStr(leak.domain)) ||
    (leak.content
      ? String(leak.content).slice(0, 60).replace(/\n/g, " ") + "…"
      : "Untitled Leak");
  const titleDisplay = cleanTitle(rawTitle) || "Active Leak Target";

  const isCritical = leak.data_types?.some((t) =>
    ["Credentials", "Financial", "PII"].includes(t)
  );
  const isHigh = leak.data_types?.some((t) =>
    ["Personal Data", "Medical"].includes(t)
  );
  const severity = isCritical
    ? "Critical"
    : isHigh
      ? "High"
      : "Low";

  const forumClean = leak.forum?.toLowerCase() || "";
  const isRansomware = 
    leak.forum_type === "ransomware" || 
    forumClean.includes("ransom") ||
    KNOWN_RANSOMWARE_GROUPS.some(group => forumClean.includes(group));

  const postType = isRansomware ? 'CYBER ATTACK' : 'DATA LEAK';
  
  // Threat actor: User requested forum that posted the leak
  const threatActor = getDisplayForum(leak.forum, leak.sectors) || leak.forum || "-";

  // Targets logic
  const hasMultipleTargets = true;
  let countryDisplay = leak.country || leak.countries?.[0] || "-";
  if (countryDisplay === "-" && (leak as any).stock_ticker) {
    const ticker = String((leak as any).stock_ticker);
    if (ticker.includes("NYSE:") || ticker.includes("NASDAQ:") || ticker.includes("AMEX:")) {
      countryDisplay = "United States";
    }
  }
  const industryDisplay = leak.primary_sector || leak.sectors?.join(", ") || (leak as any).category || (leak as any).industry || leak.sector || "-";
  const organizationDisplay = resolveStr(leak.company_name) || resolveStr((leak as any).company) || resolveStr(leak.victim) || (isRansomware ? resolveStr(leak.title) : (resolveStr(leak.domain) || "-"));
  const siteDisplay = resolveStr(leak.site) || resolveStr(leak.website) || "-";
  
  // Show "multiple" if there's sufficient data, otherwise fallback
  const targetsDisplay = "multiple"; 

  const collectedRaw = leak.collected_at || leak.first_seen_at || leak.created_at;
  const collectedDate = collectedRaw ? new Date(collectedRaw).toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric"
  }) : "-";

  return (
    <tr
      className={`group border-b border-slate-800/40 hover:bg-slate-800/20 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-800/10' : ''}`}
      onClick={() => onRowClick?.(leak, isLocked, index)}
    >
      {/* Index */}
      <td className="py-6 pl-5 pr-2 text-[13px] font-medium text-slate-400 align-top whitespace-nowrap">
        {index}
      </td>

      {/* Post */}
      <td className="py-6 pl-2 pr-10 align-top">
        <div className="flex flex-col space-y-2">
          <span className="text-[15px] font-medium text-slate-200 leading-snug whitespace-normal break-words">
            {titleDisplay}
          </span>
          
          <div className="flex items-center gap-2 pt-1 mt-1">
            <span className="text-[11px] font-bold text-slate-400 tracking-wider">
              POST-{leak._id.substring(leak._id.length - 8).toUpperCase()}
            </span>
            <span className="px-2 py-[2px] rounded-full bg-slate-800 text-slate-300 text-[10px] font-bold tracking-wider whitespace-nowrap">
              {postType}
            </span>
          </div>
        </div>
      </td>

      {/* Threat Actors & Targets */}
      {isLocked ? (
        <td colSpan={2} className="py-6 px-4 align-middle text-center">
          <div className="flex items-center justify-center">
            <UpgradeToSeeMoreButton />
          </div>
        </td>
      ) : (
        <>
          {/* Threat Actors */}
          <td className="py-6 px-4 align-top">
            <span className="text-[13px] font-medium text-slate-300 border-b border-dashed border-slate-500/80 pb-0.5 inline-block">
              {threatActor}
            </span>
          </td>

          {/* Targets */}
          <td className="py-6 px-4 align-top">
            <div className="flex flex-col">
              {!isExpanded && (
                <span 
                  className="text-[13px] font-medium text-cyan-400 flex items-center gap-1 cursor-pointer hover:text-cyan-300 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(true);
                  }}
                >
                  {targetsDisplay} <ChevronDown size={14} className="text-cyan-600" />
                </span>
              )}
              <AnimatePresence>
                {isExpanded && (
                  <motion.div 
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="flex flex-col space-y-4 overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-col space-y-1">
                      <span className="text-[10px] font-bold text-slate-500 tracking-widest uppercase">Country</span>
                      <span className="text-[13px] font-medium text-slate-300">{countryDisplay}</span>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <span className="text-[10px] font-bold text-slate-500 tracking-widest uppercase">Industry</span>
                      <span className="text-[13px] font-medium text-slate-300">{industryDisplay}</span>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <span className="text-[10px] font-bold text-slate-500 tracking-widest uppercase">Organization</span>
                      <span className="text-[13px] font-medium text-slate-300">{organizationDisplay}</span>
                    </div>
                    <div className="flex flex-col space-y-1">
                      <span className="text-[10px] font-bold text-slate-500 tracking-widest uppercase">Site</span>
                      <span className="text-[13px] font-medium text-slate-300">{siteDisplay}</span>
                    </div>
                    <div 
                      className="text-[13px] font-medium text-cyan-400 flex items-center gap-1 cursor-pointer hover:text-cyan-300 pt-2 transition-colors"
                      onClick={() => setIsExpanded(false)}
                    >
                      less <ChevronUp size={14} className="text-cyan-600" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </td>
        </>
      )}

      {/* Collected At */}
      <td className="py-6 px-4 text-[13px] font-medium text-slate-300 align-top whitespace-nowrap">
        {collectedDate}
      </td>

      {/* Share Button (Admin only, at last) */}
      {isAdmin && (
        <td className="py-6 px-2 align-top whitespace-nowrap text-center" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onShare?.(leak);
            }}
            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-slate-800/80 hover:bg-slate-700/80 border border-slate-700/70 hover:border-cyan-500/50 text-slate-400 hover:text-white transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-cyan-500/40 shadow-sm"
            title="Prepare X post"
            aria-label="Prepare X post"
          >
            <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </button>
        </td>
      )}

      {/* Row Navigation Chevron */}
      <td className="py-6 pr-5 pl-1 align-top text-right">
        <div className="flex justify-end items-center pt-1.5">
          <ChevronRight size={18} className="text-slate-500 group-hover:text-slate-300 transition-colors" />
        </div>
      </td>
    </tr>
  );
}

// ── Table ─────────────────────────────────────────────────────────────────────
export default function LeakTable({
  leaks,
  loading,
  isAdmin = false,
  user,
  hasSearch = false,
  onRowClick,
  onShare,
  startIndex = 1,
}: LeakTableProps) {
  const [activeShareLeak, setActiveShareLeak] = useState<Leak | null>(null);

  const handleShare = (leak: Leak) => {
    if (onShare) {
      onShare(leak);
    } else {
      setActiveShareLeak(leak);
    }
  };

  if (loading) {
    return (
      <div className="w-full bg-[#171717] rounded-xl border border-slate-800/60 p-4">
        <div className="animate-pulse">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-[80px] border-b border-slate-800/40 bg-slate-800/10"
            />
          ))}
        </div>
      </div>
    );
  }

  if (leaks.length === 0) {
    return (
      <div className="w-full flex flex-col items-center justify-center py-20 text-center border border-slate-800/60 rounded-xl bg-[#171717]">
        <div className="text-4xl mb-4">🔍</div>
        <div className="text-lg font-semibold text-white mb-2">No posts found</div>
        <div className="text-sm text-slate-400">
          Try adjusting your filters or search query
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full overflow-x-auto border border-slate-800/60 rounded-xl bg-[#171717]">
        <table className="w-full text-left border-collapse min-w-[960px] table-fixed">
          <thead>
            <tr className="border-b border-slate-800/60">
              <th className={`py-4 pl-5 pr-2 text-[11px] font-bold text-slate-400 tracking-widest uppercase whitespace-nowrap ${isAdmin ? 'w-[4%]' : 'w-[4%]'}`}>
                #
              </th>
              <th className={`py-4 pl-2 pr-10 text-[11px] font-bold text-slate-400 tracking-widest uppercase ${isAdmin ? 'w-[32%]' : 'w-[35%]'}`}>
                Post
              </th>
              <th className={`py-4 px-4 text-[11px] font-bold text-slate-400 tracking-widest uppercase whitespace-nowrap ${isAdmin ? 'w-[18%]' : 'w-[19%]'}`}>
                Threat Actors
              </th>
              <th className={`py-4 px-4 text-[11px] font-bold text-slate-400 tracking-widest uppercase whitespace-nowrap ${isAdmin ? 'w-[18%]' : 'w-[19%]'}`}>
                Targets
              </th>
              <th className={`py-4 px-4 text-[11px] font-bold text-slate-400 tracking-widest uppercase whitespace-nowrap ${isAdmin ? 'w-[18%]' : 'w-[19%]'}`}>
                Collected At
              </th>
              {isAdmin && (
                <th className="py-4 px-2 text-[11px] font-bold text-slate-400 tracking-widest uppercase whitespace-nowrap text-center w-[6%]">
                  Share
                </th>
              )}
              <th className="py-4 pr-5 pl-1 w-[4%]"></th>
            </tr>
          </thead>
          <tbody>
            {leaks.map((leak, idx) => (
              <LeakRow
                key={leak._id}
                leak={leak}
                index={startIndex + idx}
                isAdmin={isAdmin}
                user={user}
                hasSearch={hasSearch}
                onRowClick={onRowClick}
                onShare={handleShare}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal before opening X */}
      <XShareModal
        leak={activeShareLeak}
        isOpen={!!activeShareLeak}
        onClose={() => setActiveShareLeak(null)}
        isAdmin={isAdmin}
      />
    </>
  );
}
